import React from 'react';
import { Button } from 'components/Elements';

export const ErrorFallback = ({ error, resetErrorBoundary }) => {
  const errorCode = error?.response?.status || 'Unknown';
  const errorMessage =
    error?.response?.data?.message || 'An unexpected error occurred.';

  const isClientError = errorCode >= 400 && errorCode < 500;
  const isServerError = errorCode >= 500;

  // Handle unauthorized errors by clearing session and redirecting to login
  if (errorCode === 401) {
    localStorage.clear();
    window.location.assign(window.location.origin);
    return null;
  }

  return (
    <div
      className="w-screen h-screen flex items-center justify-center bg-gradient-to-br from-black to-[#0c0c0c]"
      role="alert"
    >
      <div className="w-full max-w-lg p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl shadow-lg text-white space-y-6">
        {/* Error Title & Message */}
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-white/90">
            System Error Detected
          </h2>
          <p className="text-white/60 text-sm leading-relaxed">
            Our systems encountered a problem while processing your request.
            Please review the details below. If the issue persists, contact your
            system administrator.
          </p>
        </div>

        {/* Error Details */}
        <div className="text-sm bg-white/10 border border-white/10 rounded-xl p-4 space-y-1">
          <p>
            <span className="text-white/40">Error Code:</span>{' '}
            <span className="font-medium text-white/90">{errorCode}</span>
          </p>
          <p>
            <span className="text-white/40">Details:</span>{' '}
            <span className="text-white/80">{errorMessage}</span>
          </p>
        </div>

        {/* Additional Insight */}
        {isClientError && (
          <p className="text-yellow-400 text-xs">
            This appears to be a client-side issue. Please verify your input or
            refresh the page.
          </p>
        )}
        {isServerError && (
          <p className="text-red-400 text-xs">
            This is a server-side issue. Our engineers have been notified and
            are investigating. We appreciate your patience.
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-wrap justify-end gap-3 pt-2">
          <Button
            variant="ghost"
            onClick={() => {
              window.history.back();
              setTimeout(() => {
                window.location.reload();
              }, 100);
            }}
          >
            ← Back
          </Button>

          <Button
            variant="glass"
            onClick={() => window.location.assign(window.location.origin)}
          >
            Retry
          </Button>

          <Button
            variant="darkGlass"
            onClick={() => {
              localStorage.clear();
              window.location.assign(window.location.origin);
            }}
          >
            Logout & Refresh
          </Button>
        </div>
      </div>
    </div>
  );
};
